import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Calendar, 
  MapPin, 
  Check, 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Users, 
  Utensils, 
  Coffee, 
  Lock, 
  Plus, 
  Search,
  Loader2,
  MessageSquare 
} from 'lucide-react';
import * as api from './api';

// --- CONFIGURATION ---
const ADMIN_PASSCODE = "cranberry25";
const APP_NAME = import.meta.env.VITE_APP_NAME || 'Wedding RSVP';

// --- Main App Component ---
export default function App() {
  const [view, setView] = useState('rsvp');
  const [loading, setLoading] = useState(true);

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleAdminAccess = (e) => {
    e.preventDefault();
    if (passcodeInput === ADMIN_PASSCODE) {
      setIsAdminAuthenticated(true);
      setShowPasscodeModal(false);
      setView('admin');
      setPasscodeError(false);
      setPasscodeInput('');
    } else {
      setPasscodeError(true);
      setPasscodeInput('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-800 font-sans selection:bg-rose-100">
      <nav className="p-6 flex justify-between items-center max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-rose-400">
          <Heart className="fill-current w-5 h-5" />
          <span className="font-serif text-2xl text-stone-800 tracking-wide">
            {APP_NAME}
          </span>
        </div>
        <div>
          {view === 'admin' ? (
            <button 
              onClick={() => {
                setView('rsvp');
                setIsAdminAuthenticated(false);
              }}
              className="text-sm font-medium hover:text-rose-500 transition-colors"
            >
              Logout Admin
            </button>
          ) : (
            <button 
              onClick={() => setShowPasscodeModal(true)}
              className="opacity-20 hover:opacity-100 transition-opacity p-2"
              title="Admin Access"
            >
              <Lock className="w-4 h-4" />
            </button>
          )}
        </div>
      </nav>

      {showPasscodeModal && (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
            <h3 className="text-xl font-serif mb-2 text-center">Admin Access</h3>
            <form onSubmit={handleAdminAccess} className="space-y-4">
              <input 
                type="password"
                placeholder="Passcode"
                className={`w-full px-4 py-3 border rounded-lg text-center ${
                  passcodeError ? 'border-red-500' : 'border-stone-200'
                }`}
                value={passcodeInput}
                onChange={(e) => {
                  setPasscodeInput(e.target.value);
                  setPasscodeError(false);
                }}
              />
              {passcodeError && (
                <p className="text-red-500 text-xs text-center">
                  Incorrect passcode
                </p>
              )}
              <div className="flex gap-2">
                <button 
                  type="button"
                  onClick={() => setShowPasscodeModal(false)}
                  className="flex-1 py-2 text-stone-500"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-2 bg-stone-800 text-white rounded-lg"
                >
                  Enter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto p-4 md:p-8">
        {view === 'rsvp' ? (
          <RSVPFlow />
        ) : (
          isAdminAuthenticated && <AdminPanel />
        )}
      </main>
    </div>
  );
}

// --- RSVP FLOW ---
function RSVPFlow() {
  const [step, setStep] = useState(0);
  const [guestData, setGuestData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    attendingWedding: null,
    guestCount: 0,
    guestNames: [],
    attendingRehearsal: null,
    attendingBreakfast: null,
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLookupSuccess = (data) => {
    setGuestData(data);
    setFormData(prev => ({
      ...prev,
      firstName: data.first_name,
      lastName: data.last_name
    }));
    setStep(1);
  };

  const hasExtraEvents =
    guestData && (guestData.invited_to_rehearsal || guestData.invited_to_breakfast);

  // ✅ CHANGED LOGIC
  const nextStep = () => {
    if (step === 1) {
      if (hasExtraEvents) {
        setStep(2);
      } else {
        setStep(3);
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  // ✅ CHANGED LOGIC
  const goBack = () => {
    if (step === 3) {
      if (hasExtraEvents) {
        setStep(2);
      } else {
        setStep(1);
      }
    } else {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.submitRSVP({
        ...formData,
        originalGuestId: guestData?.id || null
      });
      setStep(4);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow-xl rounded-2xl border border-stone-100">
      <div className="p-6 md:p-12 min-h-[400px]">
        {step === 0 && <LookupStep onFound={handleLookupSuccess} />}
        {step === 1 && (
          <MainDetailsStep
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && (
          <ExtraEventsStep
            formData={formData}
            setFormData={setFormData}
            guestData={guestData}
            onNext={nextStep}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <FinalCommentsStep
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onBack={goBack}
            isSubmitting={isSubmitting}
          />
        )}
        {step === 4 && <SuccessStep />}
      </div>
    </div>
  );
}

function LookupStep({ onFound }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    
    setSearching(true);
    setError('');

    try {
      const guests = await api.lookupGuest(firstName, lastName);
      
      if (guests && guests.length > 0) {
        // Find exact match on first name (case insensitive)
        const found = guests.find(g => 
          g.first_name.toLowerCase() === firstName.trim().toLowerCase()
        );
        
        if (found) {
          onFound(found);
        } else {
          setError("We couldn't find your name on the guest list. Please check the spelling or try your partner's name.");
        }
      } else {
        setError("We couldn't find your name on the guest list. Please check the spelling or try your partner's name.");
      }
    } catch (err) {
      console.error(err);
      // Show more detailed error for debugging
      const errorMessage = err.message || 'Unknown error';
      setError(`Unable to connect to the guest list. (${errorMessage})`);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="max-w-md mx-auto text-center space-y-8 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-3xl font-serif text-stone-800">Welcome!</h2>
        <p className="text-stone-500">Please enter your name to unlock your RSVP.</p>
      </div>

      <form onSubmit={handleSearch} className="space-y-4 text-left">
        <div>
          <label className="block text-sm font-medium text-stone-600 mb-1">First Name</label>
          <input 
            type="text" 
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-rose-200 focus:border-rose-300 outline-none transition-all"
            placeholder="e.g. Jane"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-600 mb-1">Last Name</label>
          <input 
            type="text" 
            required
            className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-rose-200 focus:border-rose-300 outline-none transition-all"
            placeholder="e.g. Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-start gap-2">
            <X className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button 
          type="submit" 
          disabled={searching}
          className="w-full bg-stone-800 hover:bg-stone-900 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {searching ? <Loader2 className="animate-spin w-4 h-4" /> : 'Find My Invitation'}
        </button>
      </form>
    </div>
  );
}

function MainDetailsStep({ formData, setFormData, onNext, onBack }) {
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGuestNameChange = (index, value) => {
    const newNames = [...formData.guestNames];
    newNames[index] = value;
    updateField('guestNames', newNames);
  };

  const handleGuestCountChange = (count) => {
    const newCount = parseInt(count) || 0;
    const currentNames = formData.guestNames;
    let newNames = [];
    for(let i=0; i<newCount; i++) {
      newNames.push(currentNames[i] || '');
    }
    setFormData(prev => ({ ...prev, guestCount: newCount, guestNames: newNames }));
  };

  const isValid = () => {
    if (!formData.email || !formData.phone) return false;
    if (formData.attendingWedding === null) return false;
    if (formData.attendingWedding === true && formData.guestCount > 0) {
      return formData.guestNames.every(name => name.trim().length > 0);
    }
    return true;
  };

  return (
    <div className="space-y-8 animate-slide-in-from-right-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-serif text-stone-800">Wedding & Reception</h2>
        <p className="text-stone-500 text-sm">Saturday, March 21st, 2025 at 2:00 PM</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-stone-400 mb-1">Email</label>
            <input 
              type="email" 
              required
              className="w-full px-3 py-2 bg-stone-50 border-b-2 border-stone-200 focus:border-rose-300 outline-none transition-colors"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-stone-400 mb-1">Phone</label>
            <input 
              type="tel" 
              required
              className="w-full px-3 py-2 bg-stone-50 border-b-2 border-stone-200 focus:border-rose-300 outline-none transition-colors"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <hr className="border-stone-100" />

        <div>
          <label className="block text-lg font-serif text-stone-800 mb-4 text-center">Will you be joining us?</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => updateField('attendingWedding', true)}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                formData.attendingWedding === true 
                  ? 'border-rose-300 bg-rose-50 text-rose-800' 
                  : 'border-stone-100 hover:border-stone-200 text-stone-500'
              }`}
            >
              <Check className={`w-6 h-6 ${formData.attendingWedding === true ? 'opacity-100' : 'opacity-30'}`} />
              <span className="font-medium">Joyfully Accept</span>
            </button>
            <button
              onClick={() => updateField('attendingWedding', false)}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                formData.attendingWedding === false 
                  ? 'border-stone-400 bg-stone-100 text-stone-800' 
                  : 'border-stone-100 hover:border-stone-200 text-stone-500'
              }`}
            >
              <X className={`w-6 h-6 ${formData.attendingWedding === false ? 'opacity-100' : 'opacity-30'}`} />
              <span className="font-medium">Regretfully Decline</span>
            </button>
          </div>
        </div>

        {formData.attendingWedding && (
          <div className="space-y-4 animate-slide-in-from-top-2">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Number of Additional Guests</label>
              <select 
                className="w-full px-4 py-2 rounded-lg border border-stone-200 bg-white"
                value={formData.guestCount}
                onChange={(e) => handleGuestCountChange(e.target.value)}
              >
                <option value="0">Just me</option>
                {[...Array(10)].map((_, i) => (
                   <option key={i + 1} value={i + 1}>{i + 1} Guest{i === 0 ? '' : 's'}</option>
                ))}
              </select>
            </div>

            {formData.guestCount > 0 && (
              <div className="space-y-3 p-4 bg-stone-50 rounded-lg">
                <p className="text-sm font-medium text-stone-500">Guest Names</p>
                {formData.guestNames.map((name, idx) => (
                  <input
                    key={idx}
                    type="text"
                    placeholder={`Guest ${idx + 1} Full Name`}
                    className="w-full px-3 py-2 rounded border border-stone-200 focus:border-rose-300 outline-none"
                    value={name}
                    onChange={(e) => handleGuestNameChange(idx, e.target.value)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between pt-6">
        <button 
          onClick={onBack}
          className="px-6 py-2 text-stone-500 hover:text-stone-800 transition-colors"
        >
          Back
        </button>
        <button 
          onClick={onNext}
          disabled={!isValid()}
          className="px-8 py-2 bg-stone-800 text-white rounded-full hover:bg-rose-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
        >
          Next Step <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ExtraEventsStep({ formData, setFormData, guestData, onNext, onBack }) {
  return (
    <div className="space-y-8 animate-slide-in-from-right-8">
       <div className="text-center space-y-2">
        <h2 className="text-2xl font-serif text-stone-800">Additional Events</h2>
        <p className="text-stone-500 text-sm">You are invited to these exclusive celebrations.</p>
      </div>

      <div className="space-y-6">
        {guestData?.invited_to_rehearsal && (
          <div className="bg-rose-50/50 p-6 rounded-xl border border-rose-100">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white p-2 rounded-full shadow-sm text-rose-400">
                <Utensils className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-stone-800">Rehearsal Dinner</h3>
                <p className="text-xs text-stone-500 uppercase tracking-wide mt-1">Friday, March 20 • 6:30 PM</p>
                <p className="text-sm text-stone-600 mt-2">Join us for an intimate dinner following the rehearsal.</p>
              </div>
            </div>
            
            <div className="flex gap-4 pl-14">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="rehearsal" 
                  className="accent-rose-500 w-4 h-4"
                  checked={formData.attendingRehearsal === true}
                  onChange={() => setFormData(prev => ({ ...prev, attendingRehearsal: true }))}
                />
                <span className="text-sm text-stone-700">I will attend</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="rehearsal" 
                  className="accent-rose-500 w-4 h-4"
                  checked={formData.attendingRehearsal === false}
                  onChange={() => setFormData(prev => ({ ...prev, attendingRehearsal: false }))}
                />
                <span className="text-sm text-stone-700">Decline</span>
              </label>
            </div>
          </div>
        )}

        {guestData?.invited_to_breakfast && (
          <div className="bg-amber-50/50 p-6 rounded-xl border border-amber-100">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white p-2 rounded-full shadow-sm text-amber-400">
                <Coffee className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-stone-800">New Beginnings Breakfast</h3>
                <p className="text-xs text-stone-500 uppercase tracking-wide mt-1">Sunday, March 22 • 8:00 AM</p>
                <p className="text-sm text-stone-600 mt-2">Wrap up the weekend with breakfast before heading home.</p>
              </div>
            </div>
            
            <div className="flex gap-4 pl-14">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="breakfast" 
                  className="accent-amber-500 w-4 h-4"
                  checked={formData.attendingBreakfast === true}
                  onChange={() => setFormData(prev => ({ ...prev, attendingBreakfast: true }))}
                />
                <span className="text-sm text-stone-700">I will attend</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="breakfast" 
                  className="accent-amber-500 w-4 h-4"
                  checked={formData.attendingBreakfast === false}
                  onChange={() => setFormData(prev => ({ ...prev, attendingBreakfast: false }))}
                />
                <span className="text-sm text-stone-700">Decline</span>
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-6">
        <button 
          onClick={onBack}
          className="px-6 py-2 text-stone-500 hover:text-stone-800 transition-colors"
        >
          Back
        </button>
        <button 
          onClick={onNext}
          className="px-8 py-2 bg-stone-800 text-white rounded-full hover:bg-rose-900 transition-all flex items-center gap-2"
        >
          Next Step <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function FinalCommentsStep({ formData, setFormData, onSubmit, onBack, isSubmitting }) {
  return (
    <div className="space-y-8 animate-slide-in-from-right-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-serif text-stone-800">Final Thoughts</h2>
        <p className="text-stone-500 text-sm">Almost there! Leave a note for the happy couple.</p>
      </div>

      <div className="space-y-6">
         <div className="flex items-center gap-3 text-stone-800 bg-stone-50 p-4 rounded-lg">
            <div className="p-2 bg-white rounded-full shadow-sm text-stone-400">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div className="text-sm">
              <p className="font-medium">Leave a message</p>
              <p className="text-stone-500 text-xs mt-0.5">Comments, questions, or just love!</p>
            </div>
         </div>

        <div>
          <label className="block text-sm font-medium text-stone-600 mb-2">Your Message</label>
          <textarea 
            className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-rose-300 outline-none h-32 resize-none"
            placeholder="Type your message here..."
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <button 
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-2 text-stone-500 hover:text-stone-800 transition-colors"
        >
          Back
        </button>
        <button 
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-8 py-2 bg-stone-800 text-white rounded-full hover:bg-rose-900 transition-all flex items-center gap-2 disabled:opacity-70"
        >
          {isSubmitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
          ) : (
            'Complete RSVP'
          )}
        </button>
      </div>
    </div>
  );
}

function SuccessStep() {
  return (
    <div className="text-center py-12 animate-zoom-in-95">
      <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Heart className="w-10 h-10 text-rose-500 fill-current" />
      </div>
      <h2 className="text-3xl font-serif text-stone-800 mb-4">Thank You!</h2>
      <p className="text-stone-600 max-w-sm mx-auto">
        Your RSVP has been successfully sent. We can't wait to celebrate with you!
      </p>
      <div className="mt-8 p-4 bg-stone-50 rounded-lg inline-block text-xs text-stone-400">
        Need to make changes? Please contact the couple directly.
      </div>
    </div>
  );
}

// --- Admin Panel Component ---

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('guests');
  const [guests, setGuests] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [newGuest, setNewGuest] = useState({ firstName: '', lastName: '', invitedToRehearsal: false, invitedToBreakfast: false });
  const [loading, setLoading] = useState(true);

  // Load data on mount
  useEffect(() => {
    loadGuests();
    loadRSVPs();
  }, []);

  const loadGuests = async () => {
    try {
      const data = await api.getGuestList(ADMIN_PASSCODE);
      setGuests(data || []);
    } catch (err) {
      console.error("Error loading guests:", err);
    }
  };

  const loadRSVPs = async () => {
    try {
      const data = await api.getRSVPs(ADMIN_PASSCODE);
      setRsvps(data || []);
    } catch (err) {
      console.error("Error loading RSVPs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    if (!newGuest.firstName || !newGuest.lastName) return;
    try {
      await api.addGuest(newGuest, ADMIN_PASSCODE);
      setNewGuest({ firstName: '', lastName: '', invitedToRehearsal: false, invitedToBreakfast: false });
      loadGuests();
    } catch (err) {
      console.error(err);
      alert(`Error adding guest: ${err.message || err}`);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden">
      <div className="bg-stone-900 text-white p-4 flex justify-between items-center">
        <h2 className="font-serif text-xl">Admin Dashboard</h2>
        <div className="flex gap-2 text-sm">
          <button 
            onClick={() => setActiveTab('guests')}
            className={`px-3 py-1 rounded ${activeTab === 'guests' ? 'bg-rose-600' : 'hover:bg-stone-700'}`}
          >
            Guest List ({guests.length})
          </button>
          <button 
             onClick={() => setActiveTab('rsvps')}
             className={`px-3 py-1 rounded ${activeTab === 'rsvps' ? 'bg-rose-600' : 'hover:bg-stone-700'}`}
          >
            RSVPs ({rsvps.length})
          </button>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-12 text-stone-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'guests' && (
              <div className="space-y-8">
                <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-stone-400 mb-3">Add New Guest</h3>
                  <form onSubmit={handleAddGuest} className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 w-full">
                      <input 
                        placeholder="First Name" 
                        className="w-full px-3 py-2 rounded border border-stone-200 text-sm"
                        value={newGuest.firstName}
                        onChange={e => setNewGuest({...newGuest, firstName: e.target.value})}
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <input 
                        placeholder="Last Name" 
                        className="w-full px-3 py-2 rounded border border-stone-200 text-sm"
                        value={newGuest.lastName}
                        onChange={e => setNewGuest({...newGuest, lastName: e.target.value})}
                      />
                    </div>
                    <div className="flex items-center gap-4 py-2">
                      <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="rounded text-rose-500 focus:ring-rose-400"
                          checked={newGuest.invitedToRehearsal}
                          onChange={e => setNewGuest({...newGuest, invitedToRehearsal: e.target.checked})}
                        /> Rehearsal
                      </label>
                      <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="rounded text-amber-500 focus:ring-amber-400"
                          checked={newGuest.invitedToBreakfast}
                          onChange={e => setNewGuest({...newGuest, invitedToBreakfast: e.target.checked})}
                        /> Breakfast
                      </label>
                    </div>
                    <button type="submit" className="bg-stone-800 text-white px-4 py-2 rounded text-sm hover:bg-stone-900 w-full md:w-auto flex items-center justify-center gap-2">
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  </form>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-stone-50 text-stone-500 font-medium">
                      <tr>
                        <th className="p-3">Name</th>
                        <th className="p-3 text-center">Rehearsal</th>
                        <th className="p-3 text-center">Breakfast</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {guests.length === 0 ? (
                        <tr><td colSpan="3" className="p-4 text-center text-stone-400">No guests added yet. Add one above!</td></tr>
                      ) : (
                        guests.map(g => (
                          <tr key={g.id}>
                            <td className="p-3 font-medium text-stone-800">{g.first_name} {g.last_name}</td>
                            <td className="p-3 text-center text-stone-400">
                              {g.invited_to_rehearsal ? <Check className="w-4 h-4 mx-auto text-rose-500" /> : '-'}
                            </td>
                            <td className="p-3 text-center text-stone-400">
                              {g.invited_to_breakfast ? <Check className="w-4 h-4 mx-auto text-amber-500" /> : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'rsvps' && (
              <div className="space-y-4">
                {rsvps.length === 0 ? (
                  <p className="text-center text-stone-400 py-8">No RSVPs received yet.</p>
                ) : (
                  rsvps.map(rsvp => (
                    <div key={rsvp.id} className="border border-stone-100 rounded-lg p-4 hover:bg-stone-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold text-stone-800">{rsvp.first_name} {rsvp.last_name}</h4>
                          <p className="text-xs text-stone-400">{rsvp.email} • {rsvp.phone}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${rsvp.attending_wedding ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {rsvp.attending_wedding ? 'ATTENDING' : 'DECLINED'}
                        </span>
                      </div>
                      
                      {rsvp.attending_wedding && (
                        <div className="text-sm text-stone-600 space-y-1 mt-2">
                           <p><span className="font-medium">Guests ({rsvp.guest_count}):</span> {rsvp.guest_names?.join(', ') || 'None'}</p>
                           {rsvp.attending_rehearsal !== null && (
                             <p className="flex items-center gap-2">
                               <Utensils className="w-3 h-3" /> Rehearsal: {rsvp.attending_rehearsal ? 'Yes' : 'No'}
                             </p>
                           )}
                           {rsvp.attending_breakfast !== null && (
                             <p className="flex items-center gap-2">
                               <Coffee className="w-3 h-3" /> Breakfast: {rsvp.attending_breakfast ? 'Yes' : 'No'}
                             </p>
                           )}
                           {rsvp.notes && (
                             <p className="mt-2 text-stone-500 italic">"{rsvp.notes}"</p>
                           )}
                        </div>
                      )}
                      <p className="text-xs text-stone-400 mt-2">
                        Submitted: {rsvp.submitted_at ? new Date(rsvp.submitted_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
